import { useMemo } from 'react';

import { SecondaryNavIdentifier } from '@zola-helpers/client/dist/es/constants/navConstants';
import { isUserPostWedding } from '@zola-helpers/client/dist/es/util/dateUtils';
import { PrimaryNavIdentifier } from '@zola-helpers/client/dist/types/constants/navConstants';

import cx from 'classnames';
import Helmet from 'react-helmet';

import { useAppSelector } from '~/store';

import UnifiedNavV2 from '../../../../components/common/header/UnifiedNavV2';
import Banner from '../../../../components/shop/Banner/Banner';
import {
  DEFAULT_DESCRIPTION,
  DEFAULT_META_TITLE,
  SHOP_PAGE_DEFAULT_OG_IMAGE,
  SITE_NAME,
  SITE_TITLE,
} from '../../constants';

type ShopHeaderProps = {
  aboveBannerContent: React.ReactNode;
  metaData?: {
    title?: string;
    description?: string;
    image?: string;
  };
  activePrimaryLink: PrimaryNavIdentifier;
  activeSecondaryLink: SecondaryNavIdentifier | undefined;
  activeTertiaryLink: SecondaryNavIdentifier | undefined;
  isBaby?: boolean;
  isWeddingBoutiquePage?: string | null;
  showBanner?: boolean;
  isPostAuthNav3Shown?: boolean;
};
export const ShopHeader = ({
  aboveBannerContent,
  metaData,
  activePrimaryLink,
  activeSecondaryLink,
  activeTertiaryLink,
  isBaby,
  isWeddingBoutiquePage,
  showBanner = true,
  isPostAuthNav3Shown,
}: ShopHeaderProps) => {
  const userContext = useAppSelector((state) => state.user.userContext);
  const isGuest = userContext ? Boolean(userContext.is_guest) : true;

  const title = useMemo(() => {
    if (!metaData?.title) return DEFAULT_META_TITLE(isBaby);

    if (metaData.title.indexOf(SITE_TITLE(isBaby)) !== -1) {
      return metaData.title;
    }

    return `${metaData.title}${SITE_TITLE(isBaby)}`;
  }, [metaData, isBaby]);

  const metaDescription = metaData?.description || DEFAULT_DESCRIPTION(isBaby);
  const image = metaData?.image || SHOP_PAGE_DEFAULT_OG_IMAGE(isBaby);

  // Determine canonical url
  const baseDomain = window.location.origin;
  const canonical = `${baseDomain}${window.location.pathname}`;

  let adjustedActiveSecondaryLink = activeSecondaryLink;
  if (!isGuest && !isBaby && !isWeddingBoutiquePage) {
    adjustedActiveSecondaryLink = isUserPostWedding(userContext ?? undefined)
      ? SecondaryNavIdentifier.POST_WEDDING_SHOP
      : SecondaryNavIdentifier.ADD_GIFTS;
  }

  return (
    <>
      <Helmet defaultTitle={title}>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={image} />
        <meta property="og:site_name" content={SITE_NAME(isBaby)} />
        <meta property="og:url" content={window.location.href} />
        {/* Social Tags */}
        <meta name="twitter:site" content="@zola" />
        <meta name="twitter:card" content="summary" />
      </Helmet>
      <UnifiedNavV2
        activePrimaryLink={activePrimaryLink}
        activeSecondaryLink={adjustedActiveSecondaryLink}
        activeTertiaryLink={activeTertiaryLink}
        isBaby={isBaby}
        isWeddingBoutiquePage={Boolean(isWeddingBoutiquePage)}
        showPromo
      />
      <div
        className={cx(
          'shop-container',
          'gridVersion',
          { isBaby: isBaby },
          { isPostAuthNav3Shown: isPostAuthNav3Shown }
        )}
      >
        {aboveBannerContent}
      </div>
      {showBanner && (
        <div className="row hidden-xs hidden-sm">
          <Banner isWeddingBoutiquePage={isWeddingBoutiquePage} />
        </div>
      )}
    </>
  );
};
